import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { FolderIcon } from '@heroicons/react/24/outline';
import { documentService } from '../../../services/documentService';
import { useToast } from '../../UI/ToastProvider';

interface NewFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPath?: string;
  onFolderCreated?: (path: string) => void;
}

export const NewFolderModal: React.FC<NewFolderModalProps> = ({
  isOpen,
  onClose,
  selectedPath,
  onFolderCreated,
}) => {
  const [folderName, setFolderName] = useState('');
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const folder = await documentService.createFolder({ name: folderName, path: selectedPath });
      addToast('Pasta criada com sucesso!', 'success');
      setFolderName('');
      onClose();
      if (onFolderCreated) {
        onFolderCreated(selectedPath || '/');
      }
    } catch (error: any) {
      addToast('Erro ao criar pasta: ' + (error?.response?.data?.message || error.message || 'Erro desconhecido'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

        <div className="relative bg-white rounded-lg max-w-md w-full mx-4 p-6">
          <Dialog.Title className="text-lg font-medium mb-4">
            Nova Pasta
          </Dialog.Title>

          <form onSubmit={handleSubmit}>
            {/* Caminho selecionado */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Localização
              </label>
              <div className="px-3 py-2 bg-gray-50 border rounded-md text-sm text-gray-600">
                {selectedPath || '/'}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome da Pasta
              </label>
              <div className="flex items-center">
                <FolderIcon className="h-5 w-5 text-yellow-500 mr-2" />
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value.slice(0, 50))}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      folderName.length >= 50 ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Digite o nome da pasta"
                    required
                    maxLength={50}
                  />
                  <div className="absolute -bottom-6 right-0 text-xs text-gray-500">
                    {folderName.length}/50
                  </div>
                </div>
              </div>
              {folderName.length >= 50 && (
                <p className="text-xs text-red-500 mt-1">
                  Limite máximo de 50 caracteres atingido
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Criando...' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
}; 